import React, { useState } from "react";
import { useDebouncedTableFilter, useModal } from "shared/hooks";
import { Page } from "shared/ui";
import { FormikProvider, useFormik } from "formik";
import { recipientListInitialValues } from "shared/constants";
import { noop } from "shared/helpers";
import { AddOrChangeRecipientSchema } from "shared/schemas";
import {
  GetReportSchedulesWithExtendedReport,
  useGetAllReportsQuery,
  useGetRecipientsListQuery,
  useGetReportsSchedulesQuery,
} from "shared/api";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { RecipientTable } from "./RecipientTable";
import { UpdateReportScheduleModal } from "./UpdateReportScheduleModal";
import { AddReportScheduleModal } from "./AddReportScheduleModal";

const RecipientList = () => {
  const navigate = useNavigate();
  const [editId, setEditId] = useState<number | null>(null);

  const [isOpenAddRecipientModal, showRecipientModal, closeRecipientModal] =
    useModal();

  const [isOpenEditEmailModal, showEditEmailModal, closeEditEmailModal] =
    useModal();

  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>([]);
  const [selectedPeriodicity, setSelectedPeriodicity] = useState<string>("");

  const handleShowEditModal = (id: number) => {
    setEditId(id);
    showEditEmailModal();
  };

  const {
    debouncedValue: debouncedReportNameValue,
    handleFilterChange: handleReportNameChange,
    value: reportNameValue,
    clearFilterValue: clearReportNameValue,
  } = useDebouncedTableFilter("recipients", 400);

  const { data: recipientLists } = useGetRecipientsListQuery();

  const { data: schedules } = useGetReportsSchedulesQuery({
    report__name__in: selectedReportTypes.length
      ? selectedReportTypes
      : undefined,
    recipients: debouncedReportNameValue || undefined,
    periodicity: selectedPeriodicity || undefined,
    expand: "report",
  }) as { data?: GetReportSchedulesWithExtendedReport[] };

  const { data: reportList } = useGetAllReportsQuery({});

  const reportTypeOptions = schedules?.map(
    (s: GetReportSchedulesWithExtendedReport) => ({
      label: s.report.name,
      value: s.report.name,
    })
  );

  const parsedRecipientValues =
    recipientLists && Array.isArray(JSON.parse(recipientLists.value))
      ? JSON.parse(recipientLists.value).map((value: string) => ({
          value,
        }))
      : [];

  const addOrChangeRecipientFormik = useFormik({
    initialValues: {
      ...recipientListInitialValues,
    },
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: AddOrChangeRecipientSchema,
    onSubmit: noop,
  });

  const usedReportIds = schedules?.map((s: any) => s.report.id) ?? [];

  const availableReports =
    reportList
      ?.filter((report) => !usedReportIds.includes(report.id))
      .map((report) => ({
        label: report.name,
        value: report.id,
        disabled: false,
      })) ?? [];

  const usedReports =
    reportList
      ?.filter((report) => usedReportIds.includes(report.id))
      .map((report) => ({
        label: report.name,
        value: report.id,
        disabled: true,
      })) ?? [];

  const formattedReportsListValue = [...availableReports, ...usedReports];

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const isVisibleClearFiltersButton =
    !!selectedReportTypes.length ||
    !!selectedPeriodicity.length ||
    debouncedReportNameValue;

  const clearAllFilter = () => {
    setSelectedReportTypes([]);
    setSelectedPeriodicity("");
    clearReportNameValue();
  };

  return (
    <FormikProvider value={addOrChangeRecipientFormik}>
      <Page
        title="Report Scheduling"
        buttonTitle="Add Report Scheduling"
        onClick={showRecipientModal}
        clearAllFilters={clearAllFilter}
        isVisibleClearFiltersButton={isVisibleClearFiltersButton}
      >
        <div className="flex gap-2 mb-2">
          <Button
            style={{ backgroundColor: themeColor }}
            onClick={() => navigate("/template-config")}
            type="primary"
          >
            Report Configuration
          </Button>

          <Button
            style={{ backgroundColor: themeColor }}
            onClick={() => navigate("/report-categories")}
            type="primary"
          >
            Report Categories
          </Button>
        </div>

        <RecipientTable
          showEditEmailModal={handleShowEditModal}
          dataSource={schedules}
          reportTypeOptions={reportTypeOptions}
          filters={{
            report: {
              value: selectedReportTypes,
              onChange: setSelectedReportTypes,
              options: reportTypeOptions,
            },
            recipient: {
              value: reportNameValue,
              onChange: handleReportNameChange,
              clear: clearReportNameValue,
            },
            periodicity: {
              value: selectedPeriodicity,
              onChange: setSelectedPeriodicity,
            },
          }}
        />

        <UpdateReportScheduleModal
          isOpen={isOpenEditEmailModal}
          closeModal={closeEditEmailModal}
          recipientOptions={parsedRecipientValues}
          scheduleId={editId}
          formattedReportsListValue={formattedReportsListValue}
        />
        <AddReportScheduleModal
          isOpen={isOpenAddRecipientModal}
          closeModal={closeRecipientModal}
          recipientOptions={parsedRecipientValues}
          formattedReportsListValue={formattedReportsListValue}
        />
      </Page>
    </FormikProvider>
  );
};

export { RecipientList };
